/**
 * Slide-over drawer for a single prospect: Information (editable) and Pricing
 * runs (read-only detail + reset + run-a-scenario). Messages tab is Phase 2.
 */
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { avatarColor, initials, StageBadge } from './bits';
import { LoDrawerShell } from './LoRadixOverlay';
import { ScenarioDetail } from './pieces';
import {
  LO_PROSPECT_POSITIONS,
  type LoProspect,
  type LoProspectEditableFields,
  type LoProspectPricingFailure
} from '@/types/lo-prospect';
import {
  getLoProspectPricingFailures,
  type ManagedUserPricingStatus,
  type PricingProfileOption,
  type RecruiterOption
} from '@/services/loProspectService';

type DrawerTab = 'info' | 'pricing';

export function LoProspectDrawer({
  prospect,
  recruiters,
  pricingProfiles = [],
  pricingProfilesEnabled = false,
  pricingProfilesError = false,
  managedUserPricingStatus = 'flag_off',
  allowInRecruitingReset = false,
  allowInRecruitingPricing = false,
  initialTab = 'info',
  busy,
  onClose,
  onSave,
  onReset,
  onRunScenario
}: {
  prospect: LoProspect;
  recruiters: RecruiterOption[];
  pricingProfiles?: PricingProfileOption[];
  pricingProfilesEnabled?: boolean;
  pricingProfilesError?: boolean;
  managedUserPricingStatus?: ManagedUserPricingStatus | 'loading';
  allowInRecruitingReset?: boolean;
  allowInRecruitingPricing?: boolean;
  initialTab?: DrawerTab;
  busy?: boolean;
  onClose: () => void;
  onSave: (fields: LoProspectEditableFields) => void;
  onReset: () => void;
  onRunScenario: () => void;
}) {
  const [tab, setTab] = useState<DrawerTab>(initialTab);
  const [d, setD] = useState<LoProspectEditableFields>({
    firstName: prospect.firstName,
    lastName: prospect.lastName,
    nmls: prospect.nmls,
    dre: prospect.dre,
    email: prospect.email,
    phone: prospect.phone,
    position: prospect.position,
    yearsExperience: prospect.yearsExperience,
    city: prospect.city,
    state: prospect.state,
    licenses: [...prospect.licenses],
    recruiterId: prospect.recruiterId,
    pricingProfileId: prospect.pricingProfileId,
    notes: prospect.notes
  });
  const [licInput, setLicInput] = useState('');
  const [pricingFailures, setPricingFailures] = useState<LoProspectPricingFailure[]>([]);
  const [pricingFailuresLoading, setPricingFailuresLoading] = useState(false);
  const [pricingFailuresError, setPricingFailuresError] = useState('');

  useEffect(() => {
    if (tab !== 'pricing') {
      return;
    }
    let active = true;
    setPricingFailuresLoading(true);
    setPricingFailuresError('');
    void getLoProspectPricingFailures(prospect.organizationId, prospect.id)
      .then((failures) => {
        if (active) {
          setPricingFailures(failures);
        }
      })
      .catch(() => {
        if (active) {
          setPricingFailuresError('Failure history could not be loaded.');
        }
      })
      .finally(() => {
        if (active) {
          setPricingFailuresLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [prospect.id, prospect.organizationId, tab]);

  const set =
    (k: keyof LoProspectEditableFields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setD((s) => ({ ...s, [k]: e.target.value }));

  const showDre = d.licenses.includes('CA') || (d.state ?? '').toUpperCase() === 'CA';

  // Pricing Profile picker: "" = follow the org default (stored NULL); explicit
  // options are the non-default profiles. Degraded (load failed / no default
  // row) renders a disabled state and the field is NOT saved — never silently
  // overwriting an assignment with an empty list's implied null.
  const defaultPricingProfile = pricingProfiles.find((p) => p.isDefault) ?? null;
  const pricingProfilesDegraded = pricingProfilesEnabled && (pricingProfilesError || !defaultPricingProfile);
  const managedPricingReadOnly = managedUserPricingStatus !== 'flag_off'
    && managedUserPricingStatus !== 'not_managed';
  const showPricingAssignment = pricingProfilesEnabled || managedPricingReadOnly;
  const managedPricingLabel = managedUserPricingStatus === 'ready'
    ? 'Prices under their own Optimal Blue seat'
    : managedUserPricingStatus === 'loading'
      ? 'Checking pricing identity…'
      : 'Optimal Blue pricing identity unavailable';

  const buildSavePatch = (): LoProspectEditableFields => {
    if (pricingProfilesEnabled && !pricingProfilesDegraded && !managedPricingReadOnly) {
      return d;
    }
    // Feature off or degraded: omit the key entirely so the update never
    // touches (or 400s on a not-yet-migrated) pricing_profile_id column.
    const { pricingProfileId: _omit, ...rest } = d;
    return rest;
  };

  const addLic = (raw: string) => {
    const code = raw.trim().toUpperCase();
    if (code && !d.licenses.includes(code)) {
      setD((s) => ({ ...s, licenses: [...s.licenses, code] }));
    }
    setLicInput('');
  };
  const removeLic = (code: string) => setD((s) => ({ ...s, licenses: s.licenses.filter((c) => c !== code) }));

  return (
    <LoDrawerShell label="Edit prospect" onClose={onClose}>
        <div className="dw-head">
          <span className="avatar" style={{ background: avatarColor(prospect.firstName + prospect.lastName) }}>
            {initials(prospect.firstName, prospect.lastName)}
          </span>
          <div className="dw-id">
            <div className="dw-name">
              {prospect.firstName} {prospect.lastName}
            </div>
            <div className="dw-sub">
              NMLS #{prospect.nmls}
              {prospect.city || prospect.state ? ` · ${prospect.city ?? ''}${prospect.city && prospect.state ? ', ' : ''}${prospect.state ?? ''}` : ''}
            </div>
          </div>
          <button className="dw-close" onClick={onClose} aria-label="Close" type="button">
            <X />
          </button>
        </div>

        <div className="dw-tabbar">
          <button className={`dw-toptab${tab === 'info' ? ' active' : ''}`} onClick={() => setTab('info')} type="button">
            Information
          </button>
          <button className={`dw-toptab${tab === 'pricing' ? ' active' : ''}`} onClick={() => setTab('pricing')} type="button">
            Pricing runs
            {prospect.runsUsed > 0 && <span className="tcount">{prospect.runsUsed}</span>}
          </button>
        </div>

        {tab === 'info' && (
          <div className="dw-body">
            <div>
              <div className="dw-sec-title">Assignment</div>
              <div className="dw-grid">
                <div className="field">
                  <label>Recruiter</label>
                  <select value={d.recruiterId ?? ''} onChange={set('recruiterId')}>
                    <option value="">Unassigned</option>
                    {recruiters.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                {showPricingAssignment && (
                  <div className="field">
                    <label>Pricing Profile</label>
                    {managedPricingReadOnly ? (
                      <div className="stage-readonly">{managedPricingLabel}</div>
                    ) : pricingProfilesDegraded ? (
                      <select aria-label="Pricing Profile" value="" disabled>
                        <option value="">Pricing profiles unavailable</option>
                      </select>
                    ) : (
                      <select aria-label="Pricing Profile" value={d.pricingProfileId ?? ''} onChange={set('pricingProfileId')}>
                        <option value="">{defaultPricingProfile?.label}</option>
                        {pricingProfiles
                          .filter((p) => !p.isDefault)
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.label}
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                )}
                <div className="field">
                  <label>Stage</label>
                  <div className="stage-readonly">
                    <StageBadge stage={prospect.stage} />
                  </div>
                </div>
                {prospect.stage === 'in_recruiting' && prospect.transitionedRecruitId && (
                  <div className="field">
                    <label>Recruit</label>
                    <div className="stage-readonly">
                      <a className="err-link" href={`/internal?section=recruiting&candidate=${prospect.transitionedRecruitId}`}>
                        Open in Recruiting
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="dw-sec-title">Loan officer</div>
              <div className="dw-grid">
                <div className="field">
                  <label>First name</label>
                  <input value={d.firstName} onChange={set('firstName')} />
                </div>
                <div className="field">
                  <label>Last name</label>
                  <input value={d.lastName} onChange={set('lastName')} />
                </div>
                <div className="field">
                  <label>Position</label>
                  <select value={d.position ?? ''} onChange={set('position')}>
                    <option value="">—</option>
                    {LO_PROSPECT_POSITIONS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field span2">
                  <label>Email</label>
                  <input value={d.email ?? ''} onChange={set('email')} />
                </div>
                <div className="field">
                  <label>Phone</label>
                  <input value={d.phone ?? ''} onChange={set('phone')} />
                </div>
                <div className="field">
                  <label>City</label>
                  <input value={d.city ?? ''} onChange={set('city')} />
                </div>
                <div className="field">
                  <label>State</label>
                  <input value={d.state ?? ''} onChange={set('state')} maxLength={2} />
                </div>
              </div>
            </div>

            <div>
              <div className="dw-sec-title">Credentials</div>
              <div className="dw-grid">
                <div className="field">
                  <label>NMLS #</label>
                  <input value={d.nmls} onChange={set('nmls')} />
                </div>
                {showDre && (
                  <div className="field">
                    <label>
                      DRE # <span className="hint">(CA)</span>
                    </label>
                    <input value={d.dre ?? ''} onChange={set('dre')} />
                  </div>
                )}
                <div className="field">
                  <label>Years experience</label>
                  <input
                    type="number"
                    value={d.yearsExperience ?? ''}
                    onChange={(e) =>
                      setD((s) => ({ ...s, yearsExperience: e.target.value === '' ? null : Number(e.target.value) }))
                    }
                  />
                </div>
                <div className="field full">
                  <label>State licenses</label>
                  <div className="lic-edit">
                    {d.licenses.map((c) => (
                      <span className="lic-chip" key={c}>
                        {c}
                        <button onClick={() => removeLic(c)} aria-label={`Remove ${c}`} type="button">
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      className="lic-add"
                      value={licInput}
                      placeholder="+ add"
                      maxLength={2}
                      onChange={(e) => setLicInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                          e.preventDefault();
                          addLic(licInput);
                        }
                      }}
                      onBlur={() => licInput && addLic(licInput)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="dw-sec-title">Recruiter notes</div>
              <div className="field">
                <textarea
                  value={d.notes}
                  onChange={set('notes')}
                  placeholder="Add a note about this prospect…"
                />
              </div>
            </div>
          </div>
        )}

        {tab === 'pricing' && (
          <div className="dw-body">
            <ScenarioDetail
              prospect={prospect}
              onReset={onReset}
              onRunScenario={onRunScenario}
              busy={busy}
              failures={pricingFailures}
              failuresLoading={pricingFailuresLoading}
              failuresError={pricingFailuresError}
              allowInRecruitingReset={allowInRecruitingReset}
              allowInRecruitingPricing={allowInRecruitingPricing}
            />
          </div>
        )}

        <div className="dw-foot">
          <span className="meta">{prospect.runsUsed} of 5 runs used</span>
          <button className="btn-sm" onClick={onClose} type="button">
            Cancel
          </button>
          <button className="btn-sm solid" onClick={() => onSave(buildSavePatch())} disabled={busy} type="button">
            Save changes
          </button>
        </div>
    </LoDrawerShell>
  );
}
